import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { PageDataPanelComponent } from 'src/app/shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from 'src/app/shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from 'src/app/shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from 'src/app/shared/page-layout/page-shell.component';
import { PageMetric } from 'src/app/shared/page-layout/page-layout.models';
import { City } from 'src/app/models/common/city.model';
import { Country } from 'src/app/models/common/country.model';
import { Customer } from 'src/app/customer/customer.model';
import { LatLng } from 'src/app/models/common/lat-lng';
import { BaseMasterDataService } from 'src/app/services/base-master-data.service';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { ContainerTrackingYardService } from '../container-tracking-yard.service';
import { ContainerTrackingYard } from '../models/container-tracking-yard.model';
import { ContainerFacilityService } from '../container-facility.service';
import { ContainerTerminalYardService } from '../container-terminal-yard.service';
import { ContainerFacility } from '../models/container-facility.model';
import { ContainerYardArea, ContainerYardChargeRule, ContainerYardSlot } from '../models/container-terminal-yard.model';

interface SetupCatalogItem {
  code: string;
  label: string;
  description: string;
  category?: string;
}

@Component({
  selector: 'app-yard',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    SelectModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    InputNumberModule,
    ToastModule,
    TableModule,
    TagModule,
    IftaLabelModule,
    TabsModule,
    ToggleSwitchModule,
    TooltipModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  templateUrl: './yard.component.html',
  styleUrl: './yard.component.css'
})
export class YardComponent implements OnInit {
  private readonly tabKeys = ['terminal-yard', 'execution-values'] as const;
  canViewYards = false;
  canCreateYard = false;
  canEditYard = false;
  canDeleteYard = false;

  yards: ContainerTrackingYard[] = [];
  displayedYards: ContainerTrackingYard[] = [];
  facilities: ContainerFacility[] = [];
  yardAreas: ContainerYardArea[] = [];
  yardSlots: ContainerYardSlot[] = [];
  chargeRules: ContainerYardChargeRule[] = [];
  totalRecords = 0;
  isLoading = true;
  activeTabIndex = 0;
  yardDialog = false;
  facilityDialog = false;
  areaDialog = false;
  slotDialog = false;
  chargeRuleDialog = false;
  yardForm!: FormGroup;
  facilityForm!: FormGroup;
  areaForm!: FormGroup;
  slotForm!: FormGroup;
  chargeRuleForm!: FormGroup;
  lastLazyEvent: TableLazyLoadEvent = { first: 0, rows: 10 };

  yardKeyword = '';
  activeFilter: 'ALL' | 'ACTIVE' | 'INACTIVE' = 'ALL';

  cities: City[] = [];
  countries: Country[] = [];
  contacts: Customer[] = [];

  readonly transportModes: SetupCatalogItem[] = [
    { code: 'TRUCK', label: 'Truck leg', description: 'Road movement between pickup, yard, warehouse, depot, or customer site.', category: 'Execution' },
    { code: 'YARD_TRANSFER', label: 'Yard transfer', description: 'Container transfer between yards, depots, or staging locations.', category: 'Execution' },
    { code: 'CUSTOMER_DELIVERY', label: 'Customer delivery', description: 'Delivery leg from yard or warehouse staging to the consignee/customer.', category: 'Execution' },
    { code: 'EMPTY_RETURN', label: 'Empty return', description: 'Return movement to the nominated empty depot or yard.', category: 'Closure' }
  ];

  readonly storageFacilityTypes: SetupCatalogItem[] = [
    { code: 'PORT_TERMINAL', label: 'Port terminal', description: 'Marine terminal for laden import/export container yard operations.', category: 'Terminal' },
    { code: 'BONDED_YARD', label: 'Bonded yard', description: 'Customs-controlled yard or bonded holding area.', category: 'Storage' },
    { code: 'CFS', label: 'CFS / transit shed', description: 'Container freight station that can bridge yard and warehouse operations.', category: 'Storage' },
    { code: 'WAREHOUSE', label: 'Warehouse staging', description: 'Full-container dwell at warehouse or CFS before destuffing.', category: 'Storage' },
    { code: 'CUSTOMER_SITE', label: 'Customer site', description: 'Container dwell at consignee or shipper premises.', category: 'Storage' },
    { code: 'EMPTY_DEPOT', label: 'Empty depot', description: 'Depot used for empty return completion.', category: 'Return' },
    { code: 'CARRIER_YARD', label: 'Carrier yard', description: 'Carrier-nominated yard for empty or laden handoff.', category: 'Storage' },
    { code: 'THIRD_PARTY_YARD', label: 'Third-party yard', description: 'External storage yard used by operations.', category: 'Storage' }
  ];

  readonly areaTypes: SetupCatalogItem[] = [
    { code: 'IMPORT_STACK', label: 'Import Stack', description: 'Inbound laden container stack.' },
    { code: 'EXPORT_STACK', label: 'Export Stack', description: 'Outbound laden container stack.' },
    { code: 'INSPECTION', label: 'Inspection', description: 'Customs, survey, or operations inspection area.' },
    { code: 'REEFER', label: 'Reefer', description: 'Plugged reefer container area.' },
    { code: 'EMPTY', label: 'Empty', description: 'Empty container holding area.' },
    { code: 'DAMAGED', label: 'Damaged', description: 'Damaged container staging area.' },
    { code: 'CUSTOMS_HOLD', label: 'Customs Hold', description: 'Hold area for customs-controlled units.' }
  ];

  readonly chargeTypes = [
    { label: 'Storage', value: 'STORAGE' },
    { label: 'Demurrage', value: 'DEMURRAGE' },
    { label: 'Detention', value: 'DETENTION' },
    { label: 'Lift On', value: 'LIFT_ON' },
    { label: 'Lift Off', value: 'LIFT_OFF' }
  ];

  readonly chargeBasisOptions = [
    { label: 'Per Day', value: 'PER_DAY' },
    { label: 'Flat', value: 'FLAT' }
  ];

  readonly legStatuses: SetupCatalogItem[] = [
    { code: 'PLANNED', label: 'Planned', description: 'Leg or storage period is scheduled but not active.' },
    { code: 'ACTIVE', label: 'Active', description: 'Current execution or dwell record.' },
    { code: 'COMPLETED', label: 'Completed', description: 'Execution is finished and contributes to closure.' },
    { code: 'CANCELLED', label: 'Cancelled', description: 'Leg or dwell was cancelled and should not drive execution.' },
    { code: 'EXCEPTION', label: 'Exception', description: 'Operational issue requires resolution or override.' }
  ];

  readonly lifecycleMilestones: SetupCatalogItem[] = [
    { code: 'CASE_OPENED', label: 'Case opened', description: 'Lifecycle record is created.', category: 'System' },
    { code: 'EMPTY_PICKUP', label: 'Pickup scheduled/started', description: 'Container pickup work has started.', category: 'Transport' },
    { code: 'GATE_OUT_EMPTY', label: 'Dispatched from pickup', description: 'Container is moving by road.', category: 'Transport' },
    { code: 'STORAGE_RECEIVED', label: 'Received at yard', description: 'Container arrived into a yard/depot/storage leg.', category: 'Storage' },
    { code: 'STORAGE_SLOT_ASSIGNED', label: 'Slot assigned', description: 'A storage slot or bay is assigned.', category: 'Storage' },
    { code: 'DESTUFFED_TO_WAREHOUSE', label: 'Destuffed to warehouse', description: 'Cargo was received into warehousing stock.', category: 'Warehousing' },
    { code: 'DELIVERED', label: 'Delivered', description: 'Container/cargo delivery milestone from road execution.', category: 'Transport' },
    { code: 'EMPTY_RETURNED', label: 'Empty returned', description: 'Return completion before normal case closure.', category: 'Closure' }
  ];

  constructor(
    private yardService: ContainerTrackingYardService,
    private facilityService: ContainerFacilityService,
    private terminalYardService: ContainerTerminalYardService,
    private fb: FormBuilder,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private permissionService: PermissionService,
    private baseMasterDataService: BaseMasterDataService,
    private sessionStorageService: SessionStorageService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.canViewYards = this.permissionService.hasPermission(app_permissions.container_tracking.yards.view);
    this.canCreateYard = this.permissionService.hasPermission(app_permissions.container_tracking.yards.create);
    this.canEditYard = this.permissionService.hasPermission(app_permissions.container_tracking.yards.edit);
    this.canDeleteYard = this.permissionService.hasPermission(app_permissions.container_tracking.yards.delete);
    this.route.queryParamMap.subscribe(params => {
      this.activeTabIndex = this.resolveTabIndex(params.get('tab'));
    });
    this.initForm();
    this.initTerminalForms();
    this.loadCountries();
    this.loadTerminalYardSetup();
    if (!this.canViewYards) {
      this.isLoading = false;
    }
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Terminal Facilities', value: this.facilities.length, hint: 'Shared facility master', tone: 'info' },
      { label: 'Yard Areas', value: this.yardAreas.length, hint: 'Operational zones', tone: 'success' },
      { label: 'Charge Rules', value: this.chargeRules.length, hint: 'Estimated yard billing', tone: 'warn' },
      { label: 'Setup Catalogs', value: this.transportModes.length + this.storageFacilityTypes.length + this.legStatuses.length, hint: 'Land execution values', tone: 'info' }
    ];
  }

  get activeFilterCount(): number {
    let count = 0;
    if (this.yardKeyword.trim()) count++;
    if (this.activeFilter !== 'ALL') count++;
    return count;
  }

  initForm(): void {
    this.yardForm = this.fb.group({
      id: [null],
      yardName: ['', Validators.required],
      address: [''],
      cityId: [null],
      countryId: [null],
      operatingHours: [''],
      contactPersonId: [null],
      latitude: [null],
      longitude: [null],
      isActive: [true]
    });
  }

  initTerminalForms(): void {
    this.facilityForm = this.fb.group({
      id: [null],
      facilityType: ['PORT_TERMINAL', Validators.required],
      facilityCode: ['', Validators.required],
      facilityName: ['', Validators.required],
      locationId: [null],
      operatorCompanyId: [null],
      linkedWarehouseId: [null],
      operatingHours: [''],
      active: [true]
    });
    this.areaForm = this.fb.group({
      id: [null],
      facilityId: [null, Validators.required],
      areaCode: ['', Validators.required],
      areaName: ['', Validators.required],
      areaType: ['IMPORT_STACK', Validators.required],
      temperatureControlled: [false],
      reeferSupported: [false],
      hazardousAllowed: [false],
      customsControlled: [false],
      active: [true]
    });
    this.slotForm = this.fb.group({
      id: [null],
      facilityId: [null, Validators.required],
      areaId: [null, Validators.required],
      slotCode: ['', Validators.required],
      blockCode: [''],
      rowCode: [''],
      bayCode: [''],
      tierCode: [''],
      maxTeuCapacity: [1, [Validators.required, Validators.min(1)]],
      allowedContainerTypes: [''],
      active: [true]
    });
    this.chargeRuleForm = this.fb.group({
      id: [null],
      facilityId: [null, Validators.required],
      areaId: [null],
      containerType: [''],
      chargeType: ['STORAGE', Validators.required],
      chargeBasis: ['PER_DAY', Validators.required],
      freeDays: [0],
      currencyCode: ['SAR', Validators.required],
      rateAmount: [0, Validators.required],
      active: [true]
    });
  }

  loadYards(event: TableLazyLoadEvent = this.lastLazyEvent): void {
    if (!this.canViewYards) {
      this.isLoading = false;
      return;
    }
    this.lastLazyEvent = event;
    this.isLoading = true;
    const rows = event.rows || 10;
    const first = event.first || 0;
    let params = new HttpParams()
      .set('page', Math.floor(first / rows).toString())
      .set('size', rows.toString());

    if (event.sortField) {
      params = params.set('sort', `${event.sortField},${event.sortOrder === 1 ? 'asc' : 'desc'}`);
    }

    this.yardService.findAll(params).subscribe({
      next: page => {
        this.yards = page.content || [];
        this.totalRecords = page.totalElements || 0;
        this.applyClientFilters();
        this.isLoading = false;
      },
      error: () => {
        this.yards = [];
        this.displayedYards = [];
        this.totalRecords = 0;
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Yards', detail: 'Unable to load container yards.' });
      }
    });
  }

  applyFilters(): void {
    this.applyClientFilters();
  }

  clearFilters(): void {
    this.yardKeyword = '';
    this.activeFilter = 'ALL';
    this.applyClientFilters();
  }

  onTabChange(value: string | number): void {
    this.activeTabIndex = Number(value);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: this.tabKeys[this.activeTabIndex] || this.tabKeys[0] },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  openNew(): void {
    if (!this.canCreateYard) {
      this.messageService.add({ severity: 'warn', summary: 'Permission', detail: 'You do not have permission to create yards.' });
      return;
    }
    this.initForm();
    this.yardDialog = true;
  }

  editYard(yard: ContainerTrackingYard): void {
    if (!this.canEditYard) {
      this.messageService.add({ severity: 'warn', summary: 'Permission', detail: 'You do not have permission to edit yards.' });
      return;
    }
    this.yardForm.patchValue({
      id: yard.id,
      yardName: yard.yardName,
      address: this.addressText(yard),
      cityId: yard.city?.id || null,
      countryId: yard.country?.id || null,
      operatingHours: yard.operatingHours,
      contactPersonId: yard.contactPerson?.id || null,
      latitude: yard.location?.latitude || null,
      longitude: yard.location?.longitude || null,
      isActive: yard.isActive
    });
    this.yardDialog = true;
  }

  deleteYard(yard: ContainerTrackingYard): void {
    if (!this.canDeleteYard) {
      this.messageService.add({ severity: 'warn', summary: 'Permission', detail: 'You do not have permission to delete yards.' });
      return;
    }
    this.confirmationService.confirm({
      message: `Delete ${yard.yardName}? Existing cases and legs may still reference this yard.`,
      header: 'Delete Yard',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.yardService.delete(yard.id!).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: 'Yard Deleted', detail: yard.yardName, life: 3000 });
            this.loadYards(this.lastLazyEvent);
          },
          error: () => this.messageService.add({ severity: 'error', summary: 'Delete Failed', detail: 'Could not delete yard.', life: 3000 })
        });
      }
    });
  }

  openNewFacility(): void {
    this.facilityForm.reset({ facilityType: 'PORT_TERMINAL', active: true });
    this.facilityDialog = true;
  }

  editFacility(facility: ContainerFacility): void {
    this.facilityForm.patchValue({
      id: facility.id,
      facilityType: facility.facilityType || 'PORT_TERMINAL',
      facilityCode: facility.facilityCode || '',
      facilityName: facility.facilityName || '',
      locationId: facility.locationId || null,
      operatorCompanyId: facility.operatorCompanyId || null,
      linkedWarehouseId: facility.linkedWarehouseId || null,
      operatingHours: facility.operatingHours || '',
      active: facility.active !== false
    });
    this.facilityDialog = true;
  }

  hideFacilityDialog(): void {
    this.facilityDialog = false;
    this.facilityForm.reset({ facilityType: 'PORT_TERMINAL', active: true });
  }

  saveFacility(): void {
    if (this.facilityForm.invalid) {
      this.facilityForm.markAllAsTouched();
      return;
    }
    const formValue = this.facilityForm.value;
    const payload: ContainerFacility = {
      ...formValue,
      owningCompanyId: this.sessionStorageService.getCompany()?.id || 0
    };
    const request = formValue.id
      ? this.facilityService.update(formValue.id, payload)
      : this.facilityService.create(payload);
    request.subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: formValue.id ? 'Facility Updated' : 'Facility Created', detail: payload.facilityName, life: 3000 });
        this.hideFacilityDialog();
        this.loadTerminalYardSetup();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: 'Could not save terminal facility.', life: 3000 })
    });
  }

  openNewArea(): void {
    this.areaForm.reset({ areaType: 'IMPORT_STACK', temperatureControlled: false, reeferSupported: false, hazardousAllowed: false, customsControlled: false, active: true });
    this.areaDialog = true;
  }

  editArea(area: ContainerYardArea): void {
    this.areaForm.patchValue({
      id: area.id,
      facilityId: area.facilityId || null,
      areaCode: area.areaCode || '',
      areaName: area.areaName || '',
      areaType: area.areaType || 'IMPORT_STACK',
      temperatureControlled: !!area.temperatureControlled,
      reeferSupported: !!area.reeferSupported,
      hazardousAllowed: !!area.hazardousAllowed,
      customsControlled: !!area.customsControlled,
      active: area.active !== false
    });
    this.areaDialog = true;
  }

  hideAreaDialog(): void {
    this.areaDialog = false;
    this.areaForm.reset({ areaType: 'IMPORT_STACK', temperatureControlled: false, reeferSupported: false, hazardousAllowed: false, customsControlled: false, active: true });
  }

  saveArea(): void {
    if (this.areaForm.invalid) {
      this.areaForm.markAllAsTouched();
      return;
    }
    const formValue = this.areaForm.value;
    const payload: ContainerYardArea = {
      ...formValue,
      owningCompanyId: this.sessionStorageService.getCompany()?.id || 0
    };
    const companyId = payload.owningCompanyId || 0;
    const request = formValue.id
      ? this.terminalYardService.updateArea(companyId, formValue.id, payload)
      : this.terminalYardService.createArea(companyId, payload);
    request.subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: formValue.id ? 'Area Updated' : 'Area Created', detail: payload.areaName, life: 3000 });
        this.hideAreaDialog();
        this.loadTerminalYardSetup();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: 'Could not save yard area.', life: 3000 })
    });
  }

  openNewSlot(): void {
    this.slotForm.reset({ maxTeuCapacity: 1, active: true });
    this.slotDialog = true;
  }

  editSlot(slot: ContainerYardSlot): void {
    this.slotForm.patchValue({
      id: slot.id,
      facilityId: slot.facilityId || null,
      areaId: slot.areaId || null,
      slotCode: slot.slotCode || '',
      blockCode: slot.blockCode || '',
      rowCode: slot.rowCode || '',
      bayCode: slot.bayCode || '',
      tierCode: slot.tierCode || '',
      maxTeuCapacity: slot.maxTeuCapacity || 1,
      allowedContainerTypes: slot.allowedContainerTypes || '',
      active: slot.active !== false
    });
    this.slotDialog = true;
  }

  hideSlotDialog(): void {
    this.slotDialog = false;
    this.slotForm.reset({ maxTeuCapacity: 1, active: true });
  }

  saveSlot(): void {
    if (this.slotForm.invalid) {
      this.slotForm.markAllAsTouched();
      return;
    }
    const formValue = this.slotForm.value;
    const payload: ContainerYardSlot = {
      ...formValue,
      owningCompanyId: this.sessionStorageService.getCompany()?.id || 0
    };
    const companyId = payload.owningCompanyId || 0;
    const request = formValue.id
      ? this.terminalYardService.updateSlot(companyId, formValue.id, payload)
      : this.terminalYardService.createSlot(companyId, payload);
    request.subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: formValue.id ? 'Slot Updated' : 'Slot Created', detail: payload.slotCode, life: 3000 });
        this.hideSlotDialog();
        this.loadTerminalYardSetup();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: 'Could not save yard slot.', life: 3000 })
    });
  }

  openNewChargeRule(): void {
    this.chargeRuleForm.reset({ chargeType: 'STORAGE', chargeBasis: 'PER_DAY', freeDays: 0, currencyCode: 'SAR', rateAmount: 0, active: true });
    this.chargeRuleDialog = true;
  }

  editChargeRule(rule: ContainerYardChargeRule): void {
    this.chargeRuleForm.patchValue(rule);
    this.chargeRuleDialog = true;
  }

  saveChargeRule(): void {
    const companyId = this.sessionStorageService.getCompany()?.id || 0;
    if (!companyId || this.chargeRuleForm.invalid) {
      this.chargeRuleForm.markAllAsTouched();
      return;
    }
    const value = this.chargeRuleForm.getRawValue();
    const request = value.id
      ? this.terminalYardService.updateChargeRule(companyId, value.id, value)
      : this.terminalYardService.createChargeRule(companyId, value);
    request.subscribe({
      next: () => {
        this.chargeRuleDialog = false;
        this.loadTerminalYardSetup();
        this.messageService.add({ severity: 'success', summary: 'Charge rule saved', detail: 'Yard charge rule was saved.' });
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Charge rule failed', detail: 'Could not save yard charge rule.' })
    });
  }

  deleteChargeRule(rule: ContainerYardChargeRule): void {
    const companyId = this.sessionStorageService.getCompany()?.id || 0;
    if (!companyId || !rule.id) {
      return;
    }
    if (!this.canDeleteYard) {
      this.messageService.add({ severity: 'warn', summary: 'Permission', detail: 'You do not have permission to delete charge rules.' });
      return;
    }
    this.confirmationService.confirm({
      header: 'Delete Charge Rule',
      message: `Delete ${rule.chargeType || 'this'} yard charge rule?`,
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.terminalYardService.deleteChargeRule(companyId, rule.id!).subscribe({
          next: () => {
            this.loadTerminalYardSetup();
            this.messageService.add({ severity: 'success', summary: 'Charge rule deleted', detail: 'Yard charge rule was deleted.' });
          },
          error: () => this.messageService.add({ severity: 'error', summary: 'Delete failed', detail: 'Could not delete yard charge rule.' })
        });
      }
    });
  }

  hideDialog(): void {
    this.yardDialog = false;
    this.yardForm.reset({ isActive: true });
  }

  saveYard(): void {
    const formValue = this.yardForm.value;
    if (!this.canCreateYard && !(this.canEditYard && formValue.id)) {
      this.messageService.add({ severity: 'warn', summary: 'Permission', detail: 'You do not have permission to save this yard.' });
      return;
    }
    if (this.yardForm.invalid) {
      this.yardForm.markAllAsTouched();
      return;
    }

    const yardPayload = this.buildYardPayload(formValue);
    const saveObservable = formValue.id
      ? this.yardService.update(formValue.id, yardPayload)
      : this.yardService.create(yardPayload);

    saveObservable.subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: formValue.id ? 'Yard Updated' : 'Yard Created',
          detail: yardPayload.yardName,
          life: 3000
        });
        this.hideDialog();
        this.loadYards(this.lastLazyEvent);
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: 'Could not save yard.', life: 3000 })
    });
  }

  addressText(yard: ContainerTrackingYard): string {
    const address = yard.address as any;
    if (!address) return '-';
    if (typeof address === 'string') return address;
    if (typeof address.getShortAddress === 'function') return address.getShortAddress();
    if (Array.isArray(address.addressElements)) {
      return address.addressElements.map((part: any) => part.name).filter(Boolean).join(', ') || '-';
    }
    return address.formattedAddress || '-';
  }

  cityLabel(yard: ContainerTrackingYard): string {
    return yard.city?.cityName || (yard.city as any)?.name || '-';
  }

  countryLabel(yard: ContainerTrackingYard): string {
    return yard.country?.name || '-';
  }

  facilityTypeLabel(type?: string): string {
    switch ((type || '').toUpperCase()) {
      case 'PORT_TERMINAL': return 'Port Terminal';
      case 'BONDED_YARD': return 'Bonded Yard';
      case 'CFS': return 'CFS / Transit Shed';
      case 'EMPTY_DEPOT': return 'Empty Depot';
      case 'CARRIER_YARD': return 'Carrier Yard';
      case 'THIRD_PARTY_YARD': return 'Third Party Yard';
      case 'WAREHOUSE': return 'Warehouse Linked';
      default: return type || '-';
    }
  }

  facilityName(facilityId?: number | null): string {
    if (!facilityId) {
      return '-';
    }
    const facility = this.facilities.find(item => item.id === facilityId);
    return facility?.facilityName || `Facility #${facilityId}`;
  }

  areaName(areaId?: number | null): string {
    if (!areaId) {
      return '-';
    }
    const area = this.yardAreas.find(item => item.id === areaId);
    return area?.areaName || area?.areaCode || `Area #${areaId}`;
  }

  statusSeverity(status?: string): string {
    switch ((status || '').toUpperCase()) {
      case 'ACTIVE':
      case 'SCHEDULED':
      case 'CHECKED_IN':
      case 'COMPLETED':
        return 'success';
      case 'ON_HOLD':
      case 'CANCELLED':
      case 'NO_SHOW':
        return 'danger';
      case 'ESTIMATED':
      case 'PER_DAY':
        return 'warn';
      default:
        return 'info';
    }
  }

  private applyClientFilters(): void {
    const keyword = this.yardKeyword.trim().toLowerCase();
    this.displayedYards = this.yards.filter(yard => {
      const activeMatches = this.activeFilter === 'ALL'
        || (this.activeFilter === 'ACTIVE' && yard.isActive)
        || (this.activeFilter === 'INACTIVE' && !yard.isActive);
      const keywordMatches = !keyword
        || (yard.yardName || '').toLowerCase().includes(keyword)
        || this.addressText(yard).toLowerCase().includes(keyword)
        || this.cityLabel(yard).toLowerCase().includes(keyword)
        || this.countryLabel(yard).toLowerCase().includes(keyword);
      return activeMatches && keywordMatches;
    });
  }

  private loadCountries(): void {
    const cachedCountries = this.sessionStorageService.getCountryList();
    if (cachedCountries?.length) {
      this.countries = cachedCountries;
      return;
    }
    this.baseMasterDataService.getCountries('en-US').subscribe({
      next: countries => this.countries = countries || [],
      error: () => this.countries = []
    });
  }

  private loadTerminalYardSetup(): void {
    const companyId = this.sessionStorageService.getCompany()?.id || 0;
    if (!companyId || !this.canViewYards) {
      this.facilities = [];
      this.yardAreas = [];
      this.yardSlots = [];
      return;
    }
    this.facilityService.findAll(new HttpParams()
      .set('owningCompanyId', String(companyId))
      .set('size', '200')
      .set('sort', 'facilityName,asc'))
      .subscribe({
        next: page => this.facilities = page.content || [],
        error: () => this.facilities = []
      });
    this.terminalYardService.findAreas(new HttpParams().set('companyId', String(companyId))).subscribe({
      next: areas => this.yardAreas = areas || [],
      error: () => this.yardAreas = []
    });
    this.terminalYardService.findSlots(new HttpParams().set('companyId', String(companyId))).subscribe({
      next: slots => this.yardSlots = slots || [],
      error: () => this.yardSlots = []
    });
    this.terminalYardService.findChargeRules(companyId).subscribe({
      next: rules => this.chargeRules = rules || [],
      error: () => this.chargeRules = []
    });
  }

  private buildYardPayload(formValue: any): ContainerTrackingYard {
    const latitude = formValue.latitude !== null && formValue.latitude !== undefined ? Number(formValue.latitude) : null;
    const longitude = formValue.longitude !== null && formValue.longitude !== undefined ? Number(formValue.longitude) : null;
    const addressText = (formValue.address || '').trim();
    return {
      id: formValue.id,
      yardName: formValue.yardName,
      address: addressText ? this.buildGeoAddress(addressText) : undefined as any,
      city: formValue.cityId ? { id: formValue.cityId } as City : undefined as any,
      country: formValue.countryId ? { id: formValue.countryId } as Country : undefined as any,
      operatingHours: formValue.operatingHours,
      contactPerson: formValue.contactPersonId ? { id: formValue.contactPersonId } as Customer : undefined as any,
      isActive: !!formValue.isActive,
      location: latitude !== null && longitude !== null ? new LatLng(latitude, longitude) : undefined as any,
      owningCompanyId: this.sessionStorageService.getCompany()?.id || 0
    };
  }

  private buildGeoAddress(value: string): any {
    return {
      addressElements: value.split(',')
        .map(part => part.trim())
        .filter(Boolean)
        .map(part => ({ name: part, types: ['route'] }))
    };
  }

  private resolveTabIndex(tab: string | null): number {
    if ((tab || '').toLowerCase() === 'yards') {
      return 0;
    }
    const index = this.tabKeys.findIndex(value => value === (tab || '').toLowerCase());
    return index >= 0 ? index : 0;
  }
}
